import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqs = [
  {
    q: 'How long does company registration take?',
    a: 'Private Limited Company registration typically takes 7–10 working days. With our express service, we can expedite it to 3–5 days. This includes name approval, DIN, DSC, MOA, AOA, and Certificate of Incorporation.',
  },
  {
    q: 'What documents are needed for GST registration?',
    a: 'You need: PAN card of business/owner, Aadhaar card, proof of business address (utility bill or rent agreement), bank account details, and a passport-size photograph. Our team guides you through the entire process.',
  },
  {
    q: 'Can I get a business loan without collateral?',
    a: 'Yes! Under the CGTMSE scheme, MSMEs can get collateral-free loans up to ₹2 crore. We help you check eligibility, prepare documents, and submit applications to 20+ partner banks and NBFCs for the best rates.',
  },
  {
    q: 'What is the Udyam (MSME) certificate and why do I need it?',
    a: 'Udyam registration gives your business official MSME recognition. Benefits include priority lending at lower interest rates, government subsidies, protection against delayed payments, and eligibility for government tenders.',
  },
  {
    q: 'Is my data safe with MSMECenter?',
    a: 'Absolutely. We use 256-bit SSL encryption, store data in ISO 27001 certified servers, and never share your information with third parties without consent. We comply with all Indian data protection regulations.',
  },
  {
    q: 'Do you offer refunds if I am not satisfied?',
    a: 'Yes, we offer a 100% money-back guarantee if we fail to deliver the service as promised. Our success rate is 99.2%, and our average customer satisfaction score is 4.9/5. Your peace of mind is our priority.',
  },
];

export function FAQ() {
  const [open, setOpen] = useState(null);

  return (
    <section className="py-12 md:py-24 bg-white dark:bg-[#0A1628] relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-bold mb-6">
            <HelpCircle className="w-4 h-4" /> Got Questions?
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Frequently Asked Questions
          </h2>
          <p className="text-slate-900 dark:text-slate-400 mt-4 text-lg">Everything you need to know about MSMECenter.</p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className={`backdrop-blur-xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                open === i
                  ? 'bg-white/[0.08] border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.1)]'
                  : 'bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none border-gray-200 dark:border-[#1E2A3A] hover:border-gray-300 dark:border-[#2A3F5F]'
              }`}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left gap-4"
              >
                <span className="text-slate-900 dark:text-white font-semibold text-base leading-snug pr-4">{faq.q}</span>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    open === i ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400' : 'bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none text-slate-900 dark:text-slate-400'
                  }`}
                >
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-6 text-slate-900 dark:text-slate-300 text-sm leading-relaxed border-t border-gray-200 dark:border-[#1E2A3A] pt-4">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
